import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { getProductRepresentativeImage } from '../services/apiService';
import { getProductNote, saveProductNote } from '../services/apiService';
import { getUserInfo } from '../utils/tokenManager';

const repImageCache = new Map();

const isGroupRow = (row) => {
  const t = row?.__type;
  return typeof t === 'string' && t.startsWith('group_');
};

function formatTripDuration(value) {
  if (value == null) return '-';
  const s = String(value).trim();
  if (!s) return '-';

  if (s.includes('시간')) {
    const m = s.match(/(-?\d+(?:\.\d+)?)/);
    if (!m) return s;
    const n = Number(m[1]);
    if (Number.isNaN(n)) return s;
    return `${Math.trunc(n)}시간`;
  }

  return s;
}

const GroupRowCellRenderer = ({ data, onToggleCity, collapsedCities }) => {
  const row = data;
  if (!row || !isGroupRow(row)) return null;

  const label = row?.country ? `${row?.city || '-'} (${row.country})` : (row?.city || '-');
  const key = String(row?.__key || '');
  const collapsed = !!collapsedCities?.has?.(key);
  const onToggle = () => {
    if (onToggleCity) onToggleCity(key);
  };

  return (
    <button
      type="button"
      className="trp-ag-group-button w-full text-left flex items-center gap-2 px-2 py-1.5 font-semibold text-gray-800 pl-4"
      onClick={(e) => { try { e.stopPropagation(); } catch (_) {} onToggle(); }}
    >
      <span className="inline-flex items-center justify-center w-4 h-4 text-gray-500">
        {collapsed ? (
          <ChevronRightIcon className="w-4 h-4" />
        ) : (
          <ChevronDownIcon className="w-4 h-4" />
        )}
      </span>
      
      <span className="truncate">{label}</span>
      
    </button>
  );
};

const ProductCellRenderer = ({ data, expandedIds, onToggleExpanded }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const noEvents = isNoEvents(data);

  const indentClass = data?.__indent === 'under_city' ? 'pl-10' : '';

  const expanded = (() => {
    try {
      const id = String(data?.상품번호 || data?.상품코드 || '');
      if (!id) return false;
      return !!expandedIds?.has?.(id);
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    const productNo = data?.상품번호;
    if (!productNo) return;

    const key = String(productNo);
    if (repImageCache.has(key)) {
      setImageUrl(repImageCache.get(key));
      return;
    }

    let alive = true;
    (async () => {
      try {
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) return;
        const url = await getProductRepresentativeImage({ companyNo, productNo });
        repImageCache.set(key, url || null);
        if (alive) setImageUrl(url || null);
      } catch {
        repImageCache.set(key, null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [data?.상품번호]);

  return (
    <div className={`flex items-center gap-3 min-w-0 ${indentClass}`}>
      <button
        type="button"
        data-trp-expand-toggle="1"
        className="p-1 rounded hover:bg-gray-100 text-gray-500"
        onPointerDown={(e) => { try { e.stopPropagation(); } catch (_) {} }}
        onMouseDown={(e) => { try { e.stopPropagation(); } catch (_) {} }}
        onClick={(e) => { e.stopPropagation(); onToggleExpanded && onToggleExpanded(data); }}
        aria-label={expanded ? '접기' : '펼치기'}
        title={expanded ? '접기' : '펼치기'}
      >
        {expanded ? (
          <ChevronDownIcon className="w-4 h-4" />
        ) : (
          <ChevronRightIcon className="w-4 h-4" />
        )}
      </button>

      <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={data?.상품명 || '대표 이미지'}
            className={`w-full h-full object-cover transition-opacity duration-200 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-full h-full" />
        )}
      </div>

      <div className="min-w-0">
        <div className="w-full flex items-center gap-2 text-left font-sm text-gray-900" title={data?.상품명 || ''}>
          {noEvents ? (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-50 text-red-600 shrink-0">
              행사 없음
            </span>
          ) : null}
          <span className="truncate">{data?.상품명 || '-'}</span>
        </div>
        <div className="text-[11px] text-gray-500 truncate">
          #{data?.상품번호 || data?.상품코드 || '-'}
          {data?.도시 ? ` · ${data.도시}` : ''}
          {data?.국가 ? ` (${data.국가})` : ''}
        </div>
      </div>
    </div>
  );
};

function isNoEvents(row) {
  try {
    const raw = row?.최종행사일;
    if (!raw) return true;
    const s = String(raw).trim();
    const d = new Date(s.includes('T') ? s : `${s}T23:59:59`);
    if (isNaN(d.getTime())) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  } catch {
    return true;
  }
}

function formatDateOnly(value) {
  try {
    if (!value) return '-';
    const str = String(value).trim();
    if (!str) return '-';
    const iso = str.includes('T')
      ? str
      : (str.length === 16 ? str.replace(' ', 'T') + ':00' : str.replace(' ', 'T'));
    const t = Date.parse(iso);
    if (isNaN(t)) {
      const m = str.match(/\d{4}-\d{2}-\d{2}/);
      return m ? m[0] : '-';
    }
    const d = new Date(t);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return '-';
  }
}

const DetailRowRenderer = ({ data, context }) => {
  const product = data?.parent;
  const { onOpenBasicSettings, onOpenPriceStock, onOpenDetailSettings, onOpenOrders, onDeleteProduct } = context || {};
  if (!product) return null;

  const manager = product?.상품담당 || product?.상품담당자 || product?.담당자 || '-';
  const currency = product?.통화 || product?.통화코드 || '-';
  const created = formatDateOnly(product?.생성일시 || product?.등록일시 || product?.createdAt);
  const badge = product?.상품뱃지 || '-';
  const detailLeftRem = 4.75;

  return (
    <div
      className="max-w-full bg-gray-50 rounded-xl px-4 py-3 mt-2"
      style={{ marginLeft: `${detailLeftRem}rem`, width: `calc(100% - ${detailLeftRem}rem)` }}
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button
          type="button"
          className="h-8 px-3 text-xs font-semibold rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
          onClick={() => onOpenBasicSettings && onOpenBasicSettings(product)}
        >
          기본설정
        </button>
        <button
          type="button"
          className="h-8 px-3 text-xs font-semibold rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
          onClick={() => onOpenPriceStock && onOpenPriceStock(product)}
        >
          가격/재고
        </button>
        <button
          type="button"
          className="h-8 px-3 text-xs font-semibold rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
          onClick={() => onOpenDetailSettings && onOpenDetailSettings(product)}
        >
          상세설정
        </button>
        <button
          type="button"
          className="h-8 px-3 text-xs font-semibold rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
          onClick={() => onOpenOrders && onOpenOrders(product)}
        >
          주문내역
        </button>
        <div className="flex-1" />
        <button
          type="button"
          className="h-8 px-3 text-xs font-semibold rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
          onClick={() => onDeleteProduct && onDeleteProduct(product)}
        >
          삭제
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
        <div className="truncate">
          <span className="text-gray-500">상품담당:</span> <span className="font-medium text-gray-800">{String(manager || '-')}</span>
        </div>
        <div className="truncate">
          <span className="text-gray-500">통화:</span> <span className="font-medium text-gray-800">{String(currency || '-')}</span>
        </div>
        <div className="truncate">
          <span className="text-gray-500">상품뱃지:</span> <span className="font-medium text-gray-800">{String(badge || '-')}</span>
        </div>
        <div className="truncate">
          <span className="text-gray-500">생성일:</span> <span className="font-medium text-gray-800">{created}</span>
        </div>
      </div>
    </div>
  );
};

function ProductInventoryGrid({ rowData = [], onToggleSale, onOpenBasicSettings, onOpenPriceStock, onOpenDetailSettings, onOpenOrders, onDeleteProduct, onNoteSaved = null }) {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [collapsedCities, setCollapsedCities] = useState(() => new Set());

  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteProduct, setNoteProduct] = useState(null);

  const openNoteModal = async (product) => {
    if (!product) return;
    const productNo = product?.상품번호 || product?.상품코드;
    if (!productNo) return;
    setNoteProduct(product);
    setNoteText('');
    setNoteLoading(true);
    setNoteModalOpen(true);
    try {
      const fetched = await getProductNote({ productNo });
      const initial = fetched || product?.상품비고 || '';
      setNoteText(String(initial || ''));
    } catch (e) {
      console.error('Failed to load product note', e);
      setNoteText(String(product?.상품비고 || ''));
    } finally {
      setNoteLoading(false);
    }
  };

  const saveNote = async () => {
    if (!noteProduct) return;
    const productNo = noteProduct?.상품번호 || noteProduct?.상품코드;
    if (!productNo) return;
    try {
      setNoteSaving(true);
      await saveProductNote({ productNo, note: noteText });
      if (onNoteSaved) onNoteSaved(noteProduct, noteText);
      setNoteModalOpen(false);
      setNoteProduct(null);
      setNoteText('');
    } catch (e) {
      console.error('Failed to save product note', e);
      alert('상품비고 저장에 실패했습니다.');
    } finally {
      setNoteSaving(false);
    }
  };

  const toggleExpanded = (product) => {
    const id = String(product?.상품번호 || product?.상품코드 || '');
    if (!id) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCityCollapsed = (cityKey) => {
    const key = String(cityKey || '');
    if (!key) return;
    setCollapsedCities((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const displayRowData = useMemo(() => {
    const arr = Array.isArray(rowData) ? rowData : [];
    const out = [];

    const cityMap = new Map();
    const norm = (v, fallback) => {
      const s = String(v ?? '').trim();
      return s ? s : fallback;
    };

    arr.forEach((p) => {
      const country = norm(p?.국가, '미지정');
      const city = norm(p?.도시, '미지정');
      const key = `${String(country)}||${String(city)}`;
      if (!cityMap.has(key)) cityMap.set(key, { country, city, list: [] });
      cityMap.get(key).list.push(p);
    });

    const groups = Array.from(cityMap.values()).sort((a, b) => {
      const c = String(a?.city || '').localeCompare(String(b?.city || ''), 'ko');
      if (c !== 0) return c;
      return String(a?.country || '').localeCompare(String(b?.country || ''), 'ko');
    });

    groups.forEach((g) => {
      const country = g?.country;
      const city = g?.city;
      const list = Array.isArray(g?.list) ? g.list : [];
      const cityKey = `${String(country)}||${String(city)}`;
      const cityCount = list.length;
      out.push({ __type: 'group_city', __key: cityKey, country, city, count: cityCount });
      if (collapsedCities.has(cityKey)) return;

      (list || []).forEach((p) => {
        const productRow = { ...p, __indent: 'under_city' };
        out.push(productRow);
        const id = String(productRow?.상품번호 || productRow?.상품코드 || '');
        if (id && expandedIds.has(id)) {
          out.push({ __type: 'detail', __parentId: id, parent: productRow });
        }
      });
    });

    return out;
  }, [rowData, expandedIds, collapsedCities]);

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      resizable: true,
      filter: false,
      headerClass: 'trp-ag-header-center',
      cellClass: 'trp-ag-cell-center',
      flex: 1,
      minWidth: 110,
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: '상품명',
        field: '상품명',
        minWidth: 360,
        flex: 2,
        sortable: false,
        filter: false,
        cellClass: 'trp-ag-cell-left',
        colSpan: (params) => {
          const row = params?.data;
          if (isGroupRow(row)) return 6;
          return 1;
        },
        cellRenderer: (params) => {
          const row = params?.data;
          if (isGroupRow(row)) {
            return (
              <GroupRowCellRenderer
                data={row}
                onToggleCity={toggleCityCollapsed}
                collapsedCities={collapsedCities}
              />
            );
          }
          return (
            <ProductCellRenderer
              data={row}
              onToggleExpanded={toggleExpanded}
              expandedIds={expandedIds}
            />
          );
        },
      },
      {
        headerName: '상품타입',
        field: '상품타입',
        minWidth: 120,
        filter: false,
        valueGetter: (p) => {
          const row = p?.data;
          if (isGroupRow(row)) return '';
          if (row?.__type === 'detail') return '';
          return row?.상품타입 || row?.상품종류 || '-';
        },
      },
      {
        headerName: '여행기간',
        field: '여행기간',
        minWidth: 110,
        maxWidth: 140,
        filter: false,
        valueGetter: (p) => {
          const row = p?.data;
          if (isGroupRow(row)) return '';
          if (row?.__type === 'detail') return '';
          return formatTripDuration(row?.여행기간);
        },
      },
      {
        headerName: '상품코드',
        field: '상품코드',
        minWidth: 140,
        filter: false,
        valueGetter: (p) => {
          const row = p?.data;
          if (isGroupRow(row)) return '';
          if (row?.__type === 'detail') return '';
          return row?.상품코드 || '-';
        },
      },
      {
        headerName: '상품비고',
        field: '상품비고',
        minWidth: 220,
        flex: 1,
        filter: false,
        valueGetter: (p) => {
          const row = p?.data;
          if (isGroupRow(row)) return '';
          if (row?.__type === 'detail') return '';
          return row?.상품비고 || '';
        },
        cellRenderer: (p) => {
          const row = p?.data;
          if (isGroupRow(row)) return null;
          if (row?.__type === 'detail') return null;
          const note = row?.상품비고 ? String(row.상품비고) : '';
          return (
            <div className="group flex items-start gap-1 w-full" onClick={(e) => { try { e.stopPropagation(); } catch (_) {} }}>
              <button
                type="button"
                className="text-center w-full cursor-pointer hover:underline decoration-dotted truncate"
                title="상품비고 편집"
                aria-label="상품비고 편집"
                onClick={() => openNoteModal(row)}
              >{note || '-'}</button>
              <svg className="mt-[2px] h-3.5 w-3.5 text-gray-400 opacity-80 group-hover:text-gray-600 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M17.414 2.586a2 2 0 0 0-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 0 0 0-2.828z"/>
                <path d="M5 15a1 1 0 0 0 1 1h9a1 1 0 1 1 0 2H6a3 3 0 0 1-3-3V6a1 1 0 1 1 2 0v9z"/>
              </svg>
            </div>
          );
        },
      },
      {
        headerName: '판매상태',
        field: '판매여부',
        minWidth: 180,
        maxWidth: 220,
        filter: false,
        sortable: false,
        cellRenderer: (p) => {
          const product = p?.data;
          const id = String(product?.상품번호 || product?.상품코드 || '');
          if (!id) return null;
          const active = !!product?.판매여부;
          const currentLabel = active ? '판매중' : '판매중지';
          const toneClass = active
            ? 'text-gray-700 border-gray-200 bg-gray-50/60'
            : 'text-amber-700 border-amber-200 bg-amber-50/60';
          return (
            <div className="flex items-center justify-center w-full" onClick={(e) => { try { e.stopPropagation(); } catch (_) {} }}>
              <select
                data-trp-row-click-ignore="1"
                className={`w-full max-w-[9.5rem] px-2 py-1 text-xs rounded border ${toneClass}`}
                defaultValue=""
                onPointerDown={(e) => { try { e.stopPropagation(); } catch (_) {} }}
                onMouseDown={(e) => { try { e.stopPropagation(); } catch (_) {} }}
                onClick={(e) => { try { e.stopPropagation(); } catch (_) {} }}
                onChange={(e) => {
                  try { e.stopPropagation(); } catch (_) {}
                  const v = e.target.value;
                  // reset back to placeholder
                  try { e.target.value = ''; } catch (_) {}
                  if (!v) return;
                  if (v === 'sale_true') {
                    if (onToggleSale) onToggleSale(product, true);
                    return;
                  }
                  if (v === 'sale_false') {
                    if (onToggleSale) onToggleSale(product, false);
                    return;
                  }
                  if (v === 'delete') {
                    const ok = window.confirm('삭제하시겠습니까?');
                    if (ok && onDeleteProduct) onDeleteProduct(product);
                  }
                }}
              >
                <option value="" disabled hidden>{currentLabel}</option>
                <option value="sale_true" style={{ color: 'rgb(4 120 87)' }}>판매중</option>
                <option value="sale_false" style={{ color: 'rgb(180 83 9)' }}>판매중지</option>
                <option value="delete" style={{ color: 'rgb(220 38 38)' }}>삭제</option>
              </select>
            </div>
          );
        },
      },
    ],
    [
      onToggleSale,
      onDeleteProduct,
      toggleExpanded,
      expandedIds,
      toggleCityCollapsed,
      collapsedCities,
    ]
  );

  const getRowId = useMemo(
    () => (params) => {
      const d = params?.data;
      if (d?.__type === 'group_city') return `group-city-${String(d.__key || 'unknown')}`;
      if (d?.__type === 'detail') return `detail-${String(d.__parentId || Math.random())}`;
      return String(d?.상품번호 || d?.상품코드 || Math.random());
    },
    []
  );

  const isFullWidthRow = useMemo(() => (params) => params?.rowNode?.data?.__type === 'detail', []);

  const fullWidthCellRenderer = useMemo(() => DetailRowRenderer, []);

  const context = useMemo(
    () => ({
      onOpenBasicSettings,
      onOpenPriceStock,
      onOpenDetailSettings,
      onOpenOrders,
      onDeleteProduct,
    }),
    [onOpenBasicSettings, onOpenPriceStock, onOpenDetailSettings, onOpenOrders, onDeleteProduct]
  );

  return (
    <>
      <div className="trp-product-inventory-grid ag-theme-quartz h-full min-h-0" style={{ width: '100%' }}>
        <AgGridReact
          rowData={displayRowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          rowClassRules={{
            'trp-ag-group-row': (p) => isGroupRow(p?.data),
            'trp-ag-group-city-row': (p) => p?.data?.__type === 'group_city',
            'trp-ag-detail-row': (p) => p?.data?.__type === 'detail',
          }}
          rowHeight={72}
          headerHeight={42}
          animateRows
          suppressCellFocus
          suppressRowClickSelection
          onRowClicked={(e) => {
            const target = e?.event?.target;
            try {
              if (
                target &&
                typeof target.closest === 'function' &&
                target.closest('[data-trp-expand-toggle="1"], [data-trp-row-click-ignore="1"]')
              ) return;
            } catch (_) {}
            const row = e?.data;
            if (!row) return;
            if (isGroupRow(row)) return;
            if (row?.__type === 'detail') return;
            toggleExpanded(row);
          }}
          isFullWidthRow={isFullWidthRow}
          fullWidthCellRenderer={fullWidthCellRenderer}
          fullWidthCellRendererParams={{}}
          context={context}
          getRowHeight={(params) => {
            const t = params?.data?.__type;
            if (t === 'detail') return 176;
            if (t === 'group_city') return 40;
            return 72;
          }}
        />
      </div>

      {noteModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setNoteModalOpen(false)} />
          <div className="relative z-10 w-[min(720px,95vw)] max-h-[90vh] overflow-auto bg-white rounded-xl shadow-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-800">상품비고 편집</h3>
              <button type="button" className="px-2 py-1 text-sm rounded border bg-white hover:bg-gray-50" onClick={() => setNoteModalOpen(false)}>닫기</button>
            </div>
            <div className="mb-3 text-sm text-gray-600">
              <div><span className="text-gray-500">상품번호:</span> <span className="font-medium">{noteProduct?.상품번호 || noteProduct?.상품코드 || '-'}</span></div>
              <div className="truncate"><span className="text-gray-500">상품명:</span> <span className="font-medium">{noteProduct?.상품명 || '-'}</span></div>
            </div>
            <div>
              {noteLoading ? (
                <div className="py-10 text-center text-sm text-gray-500">불러오는 중...</div>
              ) : (
                <textarea
                  className="w-full h-64 border rounded-lg p-3 text-sm"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="상품비고를 입력하세요"
                />
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="px-3 py-2 text-sm rounded border bg-white hover:bg-gray-50" onClick={() => setNoteModalOpen(false)}>취소</button>
              <button type="button" className={`px-3 py-2 text-sm rounded ${noteSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`} onClick={saveNote} disabled={noteSaving}>{noteSaving ? '저장 중...' : '저장'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProductInventoryGrid;
